import { BuildComponentTypeDefinition, BaseType } from "../types/Enums";
import { translate } from "../types/EnumTranslations";

// Singleton storage for component types
let componentTypes: Record<string, BuildComponentTypeDefinition> = {};

/**
 * Updates the global store with new component types.
 * Should be called when data is fetched (e.g., by BuildComponentTypesContext).
 */
export const setBuildComponentTypes = (types: Record<string, BuildComponentTypeDefinition>) => {
    componentTypes = types;
};

/**
 * Synchronously retrieves the translation for a given component type key.
 * When the specific type can't be resolved, falls back to the component's
 * baseType translation (if provided) so we show e.g. "Fasad" instead of
 * "Odefinierat". The raw key is internal metadata and must never be shown.
 */
export const getBuildComponentTypeTranslation = (
    key: string | undefined,
    fallbackBaseType?: BaseType | string
): string => {
    const fallback = () => (fallbackBaseType ? translate(fallbackBaseType) : "Odefinierat");

    if (!key) return fallback();

    const typeDef = componentTypes[key];
    if (!typeDef) return fallback();

    return typeDef.translations?.sv || fallback();
};

/**
 * The Swedish name of a component type, or undefined when the key is unknown
 * or has no translation — for callers that leave an unresolved type out
 * instead of showing a fallback word.
 */
export const resolveBuildComponentTypeTranslation = (key: string | null | undefined): string | undefined => {
    if (!key) return undefined;
    return componentTypes[key]?.translations?.sv || undefined;
};

/**
 * Synchronously retrieves the BaseType for a given component type key.
 */
export const getBuildComponentBaseType = (key: string): BaseType | undefined => {
    const typeDef = componentTypes[key];
    return typeDef?.baseType as BaseType | undefined;
};

/**
 * Get all loaded types
 */
export const getAllBuildComponentTypes = (): Record<string, BuildComponentTypeDefinition> => {
    return componentTypes;
};
